import type { Transaction, Auth } from 'oscar-typedef'
import { AptosPrepareData } from '../prepareData/AptosPrepareData'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import {
  type ITransactionExecutor,
  type EncUVD,
  type AptosSendExtraData,
  AccountToken,
} from '@/types'

export class AptosTokenTransactionExecutor implements ITransactionExecutor {
  private accountToken: AccountToken

  constructor(params: { accountToken: AccountToken }) {
    this.accountToken = params.accountToken
  }

  async getPrepareData(params: {
    to: string
    amount: string
  }): Promise<AptosPrepareData> {
    const data =
      await WepinBackendAPI.getInstance().transaction.getTransactionPrepareData(
        {
          accountId: this.accountToken.accountId,
          tokenId: this.accountToken.tokenId?.toString(),
          contract: this.accountToken.coin?.contract ?? undefined,
          to: params.to,
          amount: params.amount,
        },
      )
    if (!data.detail) {
      throw new Error('No detail data for prepare')
    }
    return new AptosPrepareData({
      gasLimit: data.detail?.gasLimit ?? 0,
      gasPrice: {
        high: data.detail?.gasPrice.high ?? 0,
        medium: data.detail?.gasPrice.medium ?? 0,
        low: data.detail?.gasPrice.low ?? 0,
      },
      maxGasAmount: data.detail?.maxGasAmount ?? 0,
      publicKey: data.detail?.publicKey,
      sequenceNumber: data.detail?.sequenceNumber,
      isRegistered: !!data.distinction?.isRegistered,
    })
  }

  async sign(params: {
    to: string
    amount: string
    encUVD: EncUVD
    extraData: AptosSendExtraData
    otpCode?: Auth.VerifyCode.Request.Body
  }): Promise<Transaction.Sign.Response.Body> {
    if (!this.accountToken.account) {
      throw new Error('Account is not exist')
    }
    if (!this.accountToken.coin) {
      throw new Error('Coin is not exist')
    }
    const result = await WepinBackendAPI.getInstance().transaction.sign({
      type: 'transaction',
      walletId: this.accountToken.account.walletId,
      accountId: this.accountToken.account.id,
      tokenId: this.accountToken.id,
      contract: this.accountToken.coin.contract,
      pin: params.encUVD,
      txData: {
        txType: 'token-transfer',
        to: params.to,
        from: this.accountToken.address,
        amount: params.amount,
        sequenceNumber: params.extraData.sequenceNumber,
        gasLimit: params.extraData.gasLimit,
        gasPrice: params.extraData.gasPrice,
        maxGasAmount: params.extraData.maxGasAmount,
        publicKey: params.extraData.publicKey,
      },
      otpCode: params.otpCode,
    })
    return result
  }

  async broadcast(params: {
    signResponse: Transaction.Sign.Response.Body
  }): Promise<Transaction.BroadcastTransaction.Response.Body> {
    if (!this.accountToken.account) {
      throw new Error('Account is not exist')
    }
    if (!this.accountToken.coin) {
      throw new Error('Coin is not exist')
    }
    return await WepinBackendAPI.getInstance().transaction.broadcastTransaction(
      {
        walletId: this.accountToken.account.walletId,
        accountId: this.accountToken.accountId,
        contract: this.accountToken.coin.contract,
        tokenId: this.accountToken.id,
        transaction: params.signResponse.transaction,
        signatureResult: params.signResponse.signatureResult,
      },
    )
  }
}
